import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';

/**
 * The register pattern, as parts.
 *
 * WHY THIS FILE EXISTS BEFORE IT IS USED TWICE
 * -------------------------------------------
 * There are twenty-three registers. The brief's step 7 says to prove the pattern
 * on ONE of them end to end before applying it anywhere, and the reason is that
 * the expensive mistake is not writing a toolbar — it is writing twenty-three
 * slightly different ones. So the *behaviour* lives here from the start and
 * `Materials.tsx` is the first caller; the second register should be markup and
 * a predicate, not a menu keyboard model written again.
 *
 * What is deliberately NOT here: anything that knows about a domain. No API
 * import, no status vocabulary, no money. A part that knew what a material was
 * would be a part the drawings register could not use, and this file would
 * quietly become a second copy of `Materials.tsx`.
 *
 * FOUR THINGS THAT ARE HARDER THAN THEY LOOK, AND ARE DONE HERE ONCE
 * -----------------------------------------------------------------
 *   1. **A menu's keyboard model.** Arrows, Home/End, Escape returning focus to
 *      the button that opened it. Written wrong, a keyboard user is trapped in a
 *      row of six buttons — which is what the overflow menu exists to remove.
 *   2. **A dialog's focus trap AND its restoration.** Tab must not walk out into
 *      the page behind, and closing must put focus back where it came from, or
 *      the reader is returned to the top of the document.
 *   3. **Unsaved-change protection.** Rendered inside the drawer rather than as
 *      `window.confirm`, so it is styled, translatable and testable, and so the
 *      choice reads in this product's own words.
 *   4. **A density preference that survives a reload**, in `localStorage`,
 *      wrapped — a browser with storage disabled throws on access and losing a
 *      row height is not worth losing the screen.
 */

// ---------------------------------------------------------------------------
// Density
// ---------------------------------------------------------------------------

export type Density = 'comfortable' | 'compact';

/**
 * A preference about the furniture, not about the data, so it stays in the
 * browser exactly as the rail's collapsed state does (`Home.tsx`). It does not
 * belong in anybody's company record.
 *
 * ONE KEY FOR EVERY REGISTER, deliberately: somebody who wants tight rows wants
 * them everywhere, and a per-register memory would mean setting it twenty-three
 * times and then finding one you missed.
 */
const DENSITY_KEY = 'fitout:register-density';

/*
 * COMPACT IS THE DEFAULT.
 *
 * It was comfortable, and a comfortable Materials row measured 91px — four
 * lines of secondary detail per record, so five materials filled a screen. A
 * register is a working document read for hours; the brief for this product
 * asks for information density, and the design system has said since the first
 * page that whitespace pushing the fourth row off the screen is a cost rather
 * than elegance.
 *
 * Comfortable is still one click away and still remembered. Nothing compact
 * hides is only available at that density — every secondary line is on the
 * record's own drawer.
 */
function readDensity(): Density {
  try {
    return window.localStorage.getItem(DENSITY_KEY) === 'comfortable' ? 'comfortable' : 'compact';
  } catch {
    return 'compact';
  }
}

function writeDensity(value: Density): void {
  try {
    window.localStorage.setItem(DENSITY_KEY, value);
  } catch {
    /* a preference is not worth an error */
  }
}

// ---------------------------------------------------------------------------
// The view state a register keeps
// ---------------------------------------------------------------------------

export interface RegisterView {
  readonly query: string;
  readonly setQuery: (value: string) => void;
  /** Filter values by their definition's key. An absent key means "all". */
  readonly chosen: Readonly<Record<string, string>>;
  readonly choose: (key: string, value: string) => void;
  readonly sortKey: string;
  readonly setSortKey: (value: string) => void;
  readonly density: Density;
  readonly setDensity: (value: Density) => void;
  /** Whether anything is narrowing the list. Drives the "showing N of M" line. */
  readonly narrowed: boolean;
  /** Back to everything, keeping the density and the sort. */
  readonly clear: () => void;
}

/**
 * The state a register's toolbar holds. STATE ONLY — it does no filtering.
 *
 * That split is on purpose. A hook that took the predicates would need them to
 * be stable across renders to memoise anything, which in practice means every
 * caller wrapping every predicate in `useCallback` and one of them forgetting.
 * Filtering in the screen keeps the dependencies visible to
 * `react-hooks/exhaustive-deps`, which this repo treats as an error.
 */
export function useRegisterView(
  defaultSort: string,
  /**
   * Filters the register STARTS with, for a register whose useful default is
   * not "everything" — Snags opens on the unfinished ones, because a snag list
   * that leads with two hundred closed snags is a snag list nobody reads.
   *
   * It is a starting point, not a floor: `clear` still goes to everything, and
   * because the toolbar counts these as narrowing, the reader is told what is
   * being held back and is one click from all of it. A default that hides rows
   * silently is the thing this must not become.
   *
   * Read ONCE, as `useState`'s initial value. Passing a fresh object literal on
   * every render is therefore safe, and changing it later does nothing.
   */
  initialFilters: Readonly<Record<string, string>> = {},
): RegisterView {
  const [query, setQuery] = useState('');
  const [chosen, setChosen] = useState<Readonly<Record<string, string>>>(initialFilters);
  const [sortKey, setSortKey] = useState(defaultSort);
  const [density, setDensityState] = useState<Density>(readDensity);

  const setDensity = useCallback((value: Density) => {
    setDensityState(value);
    writeDensity(value);
  }, []);

  const choose = useCallback((key: string, value: string) => {
    setChosen((current) => {
      const next = { ...current };
      // An empty value means "all", and it is REMOVED rather than stored as ''.
      // Otherwise `narrowed` counts a filter nobody set.
      if (value) next[key] = value;
      else delete next[key];
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    setQuery('');
    setChosen({});
  }, []);

  return {
    query,
    setQuery,
    chosen,
    choose,
    sortKey,
    setSortKey,
    density,
    setDensity,
    narrowed: query.trim().length > 0 || Object.keys(chosen).length > 0,
    clear,
  };
}

/** Case- and punctuation-tolerant match. "MAT 7" finds "MAT-0007". */
export function matches(haystack: string, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  const hay = haystack.toLowerCase();
  // Every word has to appear somewhere. "desk joinery" finds a reception desk in
  // joinery whichever order the row happens to name them in.
  return needle.split(/\s+/).every((word) => hay.includes(word));
}

// ---------------------------------------------------------------------------
// The toolbar
// ---------------------------------------------------------------------------

export interface FilterDef {
  readonly key: string;
  readonly label: string;
  readonly options: readonly { readonly value: string; readonly label: string }[];
}

export interface ToolbarProps {
  readonly view: RegisterView;
  /** What the search box is searching, in the reader's words. */
  readonly searchLabel: string;
  readonly searchPlaceholder?: string;
  readonly filters?: readonly FilterDef[];
  readonly sorts?: readonly { readonly key: string; readonly label: string }[];
  /** How many rows are on screen, and how many are LOADED. */
  readonly shown: number;
  readonly total: number;
  readonly noun: readonly [string, string];
  /**
   * How many rows exist on the server, when that differs from `total`.
   *
   * The server bounds a register (`api/paging.ts`), and this screen searches,
   * filters and sorts across what it has LOADED. So when only part is loaded
   * the toolbar has to say so — a search box quietly looking at 500 of 1,240
   * rows returns "no matches" for a row that exists, and that reads as an
   * answer rather than a fault.
   *
   * Absent means "this register is not bounded yet", which is the honest
   * reading for a screen not yet converted.
   */
  readonly registerTotal?: number;
  /** Fetch the rest. Absent means the screen cannot, so nothing is offered. */
  readonly onLoadAll?: () => void;
  readonly loadingAll?: boolean;
  /** Rendered at the end. The export button goes here, where permitted. */
  readonly children?: ReactNode;
}

export function RegisterToolbar({
  view,
  searchLabel,
  searchPlaceholder,
  filters = [],
  sorts = [],
  shown,
  total,
  noun,
  registerTotal,
  onLoadAll,
  loadingAll = false,
  children,
}: ToolbarProps): JSX.Element {
  const base = useId();
  const partial = registerTotal !== undefined && registerTotal > total;

  return (
    <div className="rk-toolbar">
      <div className="rk-field rk-search">
        <label htmlFor={`${base}-q`}>{searchLabel}</label>
        {/*
          `type="search"` gives a native clear affordance on every browser this
          runs in, and 16px is the floor for anything typed into — below it iOS
          zooms the page on focus and the reader loses sight of the register.
        */}
        <input
          id={`${base}-q`}
          type="search"
          value={view.query}
          placeholder={searchPlaceholder}
          onChange={(event) => view.setQuery(event.target.value)}
        />
      </div>

      {filters.map((filter) => (
        <div className="rk-field" key={filter.key}>
          <label htmlFor={`${base}-${filter.key}`}>{filter.label}</label>
          <select
            id={`${base}-${filter.key}`}
            value={view.chosen[filter.key] ?? ''}
            onChange={(event) => view.choose(filter.key, event.target.value)}
          >
            <option value="">All</option>
            {filter.options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      ))}

      {sorts.length > 0 && (
        <div className="rk-field">
          <label htmlFor={`${base}-sort`}>Sort by</label>
          <select
            id={`${base}-sort`}
            value={view.sortKey}
            onChange={(event) => view.setSortKey(event.target.value)}
          >
            {sorts.map((sort) => (
              <option key={sort.key} value={sort.key}>
                {sort.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {/*
        Two buttons rather than a switch. `aria-pressed` says which one is on, so
        the state is announced rather than inferred from a colour — and a toggle
        whose label changes ("Compact" / "Comfortable") makes a screen reader
        read out the thing it is ABOUT to do as though it were the current state.
      */}
      <div className="rk-field rk-density" role="group" aria-label="Row height">
        <span className="rk-field-label" id={`${base}-density`}>
          Rows
        </span>
        <div className="rk-density-buttons">
          <button
            type="button"
            className="rk-density-button"
            aria-pressed={view.density === 'comfortable'}
            onClick={() => view.setDensity('comfortable')}
          >
            Comfortable
          </button>
          <button
            type="button"
            className="rk-density-button"
            aria-pressed={view.density === 'compact'}
            onClick={() => view.setDensity('compact')}
          >
            Compact
          </button>
        </div>
      </div>

      <p className="rk-count" role="status">
        {view.narrowed ? (
          <>
            Showing <strong>{shown}</strong> of {total} {total === 1 ? noun[0] : noun[1]}
            {'. '}
            <button type="button" className="link-button" onClick={view.clear}>
              Show all
            </button>
          </>
        ) : (
          <>
            {total} {total === 1 ? noun[0] : noun[1]}
          </>
        )}
      </p>

      {/*
        Said whenever only part of the register is loaded, and said BEFORE
        somebody searches rather than after they conclude a record is missing.
        The sentence names the consequence — the search only looks at what is
        loaded — because "500 of 1,240" on its own reads as a performance note
        rather than a warning about the answers.
      */}
      {partial && (
        <p className="rk-partial" role="status">
          Only the first <strong>{total}</strong> of {registerTotal} {noun[1]} are loaded, so the
          search and filters above are only looking at those.{' '}
          {onLoadAll && (
            <button type="button" className="link-button" onClick={onLoadAll} disabled={loadingAll}>
              {loadingAll ? 'Loading…' : 'Load the rest'}
            </button>
          )}
        </p>
      )}

      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Row actions: one visible, the rest behind a menu
// ---------------------------------------------------------------------------

export interface RowAction {
  /** The visible words. Reads as an instruction: "Mark ordered". */
  readonly label: string;
  readonly onSelect: () => void;
  readonly danger?: boolean;
  readonly disabled?: boolean;
}

export interface RowActionsProps {
  /** The one action that stays visible. */
  readonly primary?: RowAction;
  readonly more?: readonly RowAction[];
  /**
   * What the row IS — a reference, a number. Every control here repeats it in
   * hidden text, because "Edit" on its own is thirty identical buttons to a
   * screen reader. It is also why the register's tests query by ROLE and not by
   * text: the hidden half makes `getByText('MAT-0007')` ambiguous.
   */
  readonly rowLabel: string;
}

/**
 * ONE VISIBLE ACTION. Everything else behind "More".
 *
 * A material can legally move to as many as four statuses at once, and with Edit,
 * Delete and Procurement beside them that was seven controls on one row — the
 * row nobody reads, which the design system names outright. The primary action
 * is the one somebody came to do; the rest are still one keystroke away and are
 * no longer competing for the eye.
 */
export function RowActions({ primary, more = [], rowLabel }: RowActionsProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  /**
   * `end` is the gap between the menu and the edge the text runs TOWARDS —
   * the right edge normally, the left one in Arabic. Measured in physical
   * pixels, because a rectangle has no other kind.
   */
  const [at, setAt] = useState<{ top: number; end: number } | null>(null);
  const menuId = useId();

  const usable = more.filter((action) => !action.disabled);

  const close = useCallback((returnFocus: boolean) => {
    setOpen(false);
    if (returnFocus) trigger.current?.focus();
  }, []);

  /**
   * POSITIONED `fixed`, FROM THE TRIGGER'S OWN RECTANGLE.
   *
   * The register's table sits in a container with `overflow: auto` — it has to,
   * for the sticky headings and the sideways scroll on a narrow screen — and an
   * absolutely-positioned menu inside an overflow container is CLIPPED by it. On
   * a long register the menu on the last visible row would have been cut off at
   * the bottom edge of the scroll box, and on a short one it would have looked
   * perfectly fine. `fixed` is outside that clip.
   *
   * The cost is that the menu does not travel with the row, so a scroll closes
   * it. That is what a native menu does, and it is the honest behaviour: a menu
   * floating over a row that is no longer the row it belongs to is worse than no
   * menu.
   */
  useLayoutEffect(() => {
    if (!open) return;
    const button = trigger.current;
    const panel = menu.current;
    if (!button || !panel) return;

    const rect = button.getBoundingClientRect();
    const height = panel.getBoundingClientRect().height;
    const room = window.innerHeight - rect.bottom;
    // Flip upward when there is no room below AND more room above. Measured
    // rather than assumed from the item count, which would tie this to a pixel
    // value in the stylesheet.
    const flip = room < height + 8 && rect.top > room;

    /*
     * The menu hangs from the trigger's INLINE END, and the offset below is the
     * only measurement in the app that has to know which physical side that is
     * (ADR-026). `getBoundingClientRect` answers in left and right whatever the
     * text direction, so the arithmetic branches here and the style stays
     * logical — see `inset-inline-end` where it is applied.
     */
    const rtl = getComputedStyle(button).direction === 'rtl';

    setAt({
      top: flip ? Math.max(8, rect.top - height - 4) : rect.bottom + 4,
      end: Math.max(8, rtl ? rect.left : window.innerWidth - rect.right),
    });
  }, [open, usable.length]);

  // A scroll or a resize moves the row out from under a `fixed` menu, so the
  // menu goes rather than lying about which row it belongs to. `capture`, because
  // the scroll happens on the table's container and does not bubble.
  useEffect(() => {
    if (!open) return;
    const shut = (): void => setOpen(false);
    window.addEventListener('scroll', shut, true);
    window.addEventListener('resize', shut);
    return () => {
      window.removeEventListener('scroll', shut, true);
      window.removeEventListener('resize', shut);
    };
  }, [open]);

  // Focus the item the arrow keys are on, so the browser's own focus ring is the
  // indicator rather than a class that can drift from where focus actually is.
  useEffect(() => {
    if (!open) return;
    const items = menu.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]');
    items?.[active]?.focus();
  }, [open, active]);

  // A click anywhere else closes it. `mousedown` rather than `click`: a click that
  // starts outside and ends inside should not count as choosing something.
  useEffect(() => {
    if (!open) return;
    function onDown(event: MouseEvent): void {
      const target = event.target as Node;
      if (menu.current?.contains(target) || trigger.current?.contains(target)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  function onMenuKey(event: ReactKeyboardEvent<HTMLDivElement>): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      close(true);
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((index) => (index + 1) % usable.length);
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((index) => (index - 1 + usable.length) % usable.length);
      return;
    }
    if (event.key === 'Home') {
      event.preventDefault();
      setActive(0);
      return;
    }
    if (event.key === 'End') {
      event.preventDefault();
      setActive(usable.length - 1);
    }
  }

  return (
    <div className="rk-actions">
      {primary && (
        <button
          type="button"
          className={primary.danger ? 'rk-action danger' : 'rk-action'}
          disabled={primary.disabled}
          onClick={primary.onSelect}
        >
          {primary.label}
          <span className="visually-hidden"> {rowLabel}</span>
        </button>
      )}

      {usable.length > 0 && (
        <>
          <button
            type="button"
            ref={trigger}
            className="rk-more"
            aria-haspopup="menu"
            aria-expanded={open}
            aria-controls={open ? menuId : undefined}
            onClick={() => {
              setActive(0);
              setAt(null);
              setOpen((was) => !was);
            }}
            onKeyDown={(event) => {
              // Down-arrow opens the menu ON the first item, which is what every
              // native menu button does and what a keyboard user will try.
              if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault();
                setActive(event.key === 'ArrowDown' ? 0 : usable.length - 1);
                setOpen(true);
              }
            }}
          >
            More
            <span className="visually-hidden"> actions for {rowLabel}</span>
            <span className="rk-caret" aria-hidden="true">
              ▾
            </span>
          </button>

          {open && (
            <div
              className="rk-menu"
              id={menuId}
              role="menu"
              ref={menu}
              aria-label={`Actions for ${rowLabel}`}
              onKeyDown={onMenuKey}
              // The first paint is off-screen-adjacent rather than mid-page: the
              // effect below measures the panel and puts it where it belongs on
              // the same frame, so nothing visible moves.
              style={
                at
                  ? { top: `${at.top}px`, insetInlineEnd: `${at.end}px` }
                  : { visibility: 'hidden' }
              }
            >
              {usable.map((action, index) => (
                <button
                  key={action.label}
                  type="button"
                  role="menuitem"
                  tabIndex={index === active ? 0 : -1}
                  className={action.danger ? 'rk-menu-item danger' : 'rk-menu-item'}
                  onClick={() => {
                    // Closed BEFORE the work, and without pulling focus back to a
                    // button that a drawer is about to move focus away from.
                    close(false);
                    action.onSelect();
                  }}
                >
                  {action.label}
                  <span className="visually-hidden"> {rowLabel}</span>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The drawer
// ---------------------------------------------------------------------------

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface DrawerProps {
  readonly title: string;
  /** A second line under the title: the record's description, its status. */
  readonly subtitle?: string;
  /**
   * Whether there are edits that would be lost. When true, Escape and the
   * backdrop ask first rather than closing.
   */
  readonly dirty?: boolean;
  readonly onClose: () => void;
  readonly children: ReactNode;
  /** Buttons. Kept out of the scrolling body so Save is always reachable. */
  readonly footer?: ReactNode;
}

/**
 * A panel over the page, for a record's detail and for a long form.
 *
 * WHY A DRAWER RATHER THAN THE INLINE PANEL IT REPLACES
 * ----------------------------------------------------
 * The register's forms opened ABOVE the table. Editing row nineteen scrolled the
 * form into view and the row out of it, so the thing being changed and the
 * change were never on screen together — and the table underneath jumped by the
 * height of the form every time one opened. A drawer leaves the register where
 * it is.
 *
 * `aria-modal` is the honest description of what this is: the page behind it is
 * not usable while it is open, so Tab has to say the same thing.
 */
export function Drawer({
  title,
  subtitle,
  dirty = false,
  onClose,
  children,
  footer,
}: DrawerProps): JSX.Element {
  const panel = useRef<HTMLDivElement>(null);
  const [confirming, setConfirming] = useState(false);
  const headingId = useId();

  /**
   * Where focus was when this opened, so it can go back.
   *
   * Read in a ref at mount rather than on close: by the time it closes, the row
   * button that opened it may have been re-rendered or removed by the reload the
   * save triggered, and `document.activeElement` would be the body.
   */
  const opener = useRef<HTMLElement | null>(null);

  useEffect(() => {
    opener.current = document.activeElement as HTMLElement | null;
    // Focus the panel itself, not the first field. Landing on a text input
    // announces the field and not what the drawer is for, and on a phone it
    // opens the keyboard over the content the reader has not read yet.
    panel.current?.focus();
    return () => {
      // `isConnected` because the opener is often a row action in a table that
      // has just been re-read from the server.
      if (opener.current?.isConnected) opener.current.focus();
    };
  }, []);

  const tryClose = useCallback(() => {
    if (dirty) setConfirming(true);
    else onClose();
  }, [dirty, onClose]);

  function onKeyDown(event: ReactKeyboardEvent<HTMLDivElement>): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      tryClose();
      return;
    }
    if (event.key !== 'Tab') return;

    // The trap. Without it, Tab from the last field walks into the register
    // behind the panel, which `aria-modal` has just told a screen reader is not
    // there.
    const items = [...(panel.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [])].filter(
      (node) => node.offsetParent !== null || node === document.activeElement,
    );
    if (items.length === 0) return;
    const first = items[0]!;
    const last = items[items.length - 1]!;

    if (event.shiftKey && (document.activeElement === first || document.activeElement === panel.current)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  return (
    <div className="rk-scrim" onMouseDown={(event) => event.target === event.currentTarget && tryClose()}>
      <div
        className="rk-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        ref={panel}
        tabIndex={-1}
        onKeyDown={onKeyDown}
      >
        <div className="rk-drawer-head">
          <div>
            <h3 className="rk-drawer-title" id={headingId}>
              {title}
            </h3>
            {subtitle && <p className="rk-drawer-sub">{subtitle}</p>}
          </div>
          <button type="button" className="rk-drawer-close" onClick={tryClose}>
            <span aria-hidden="true">✕</span>
            <span className="visually-hidden">Close {title}</span>
          </button>
        </div>

        <div className="rk-drawer-body">
          {/*
           * Unsaved-change protection, in this product's own words rather than
           * `window.confirm`'s. "Discard" is the destructive option and says what
           * it destroys; "Keep editing" is first because it is the safe one and
           * the reader arrived here by accident.
           *
           * IT SITS ABOVE THE FORM AND THE FORM STAYS MOUNTED. Rendering this
           * INSTEAD of the children unmounted the form, so "Keep editing" threw
           * away exactly the changes it had just promised to keep — the typed
           * value was gone and the field had reverted to the record. Caught by a
           * test rather than by reading, because the prompt itself looked right.
           */}
          {confirming && (
            <div className="rk-guard">
              <div className="alert" role="alert">
                <span>You have changes that have not been saved.</span>
              </div>
              <div className="button-row">
                <button
                  type="button"
                  className="inline"
                  autoFocus
                  onClick={() => setConfirming(false)}
                >
                  Keep editing
                </button>
                <button
                  type="button"
                  className="secondary inline danger"
                  onClick={() => {
                    setConfirming(false);
                    onClose();
                  }}
                >
                  Discard the changes
                </button>
              </div>
            </div>
          )}
          {children}
        </div>
        {footer && <div className="rk-drawer-foot">{footer}</div>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

/**
 * The rows on screen, as a CSV file the browser saves.
 *
 * BUILT IN THE BROWSER, AND WHY THAT IS NOT A GAP
 * ----------------------------------------------
 * These bytes are already here. The server has filtered the rows by permission
 * and omitted the financial fields this caller may not read (ADR-005) before
 * they ever reached the screen, so a file of what is on screen cannot contain
 * anything the screen does not. It also exports what the FILTERS show, which is
 * what somebody taking a register into a meeting actually wants.
 *
 * IT IS NOW AUDITED TOO — see `register-export.ts`.
 *
 * This note used to end "if a register export ever needs to be audited, this
 * becomes a route; it is not one today". `09` §Threats had already asked for
 * it: *"A user bulk-exports the whole project before resigning"* is answered by
 * *"every export is audited with row count and filters"*, and the registers are
 * where the project actually lives. So there is a route, the permission is
 * checked on it, and the count and filters the browser reports are recorded as
 * reported — which is honest, and enough for the case `09` describes.
 *
 * These two functions still know nothing about it. `exportRegister` composes
 * them with the call that records the act, one file away, so this file stays
 * free of the API client.
 */
export function toCsv(headers: readonly string[], rows: readonly (readonly string[])[]): string {
  const cell = (value: string): string =>
    // Quote when it would otherwise break the row, and double an inner quote.
    // A leading `=`, `+`, `-` or `@` is prefixed with an apostrophe: a
    // spreadsheet treats those as formulas, and a supplier name is not one.
    /^[=+\-@]/.test(value) || /["\n\r,]/.test(value)
      ? `"${(/^[=+\-@]/.test(value) ? `'${value}` : value).replace(/"/g, '""')}"`
      : value;

  return [headers, ...rows].map((row) => row.map(cell).join(',')).join('\r\n');
}

/**
 * U+FEFF, NAMED rather than typed.
 *
 * Excel on Windows reads a CSV without a byte-order mark as the system code
 * page, which turns every Arabic supplier name in the register into mojibake.
 * It is built from its code point because a literal BOM in source is an
 * invisible character in a string literal — `no-irregular-whitespace` refuses
 * it, and is right to.
 */
const BOM = String.fromCharCode(0xfeff);

/** Hand the CSV to the browser as a download. */
export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
